import { FC, useEffect, useState } from 'react';
import {
    Box,
    Button,
    Checkbox,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControl,
    FormControlLabel,
    FormHelperText,
    Grid,
    InputLabel,
    OutlinedInput,
    TextField,
    Typography,
    useMediaQuery,
    useTheme
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { Firmware } from 'types/firmware';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import { dispatch } from 'store';
import { startSnackbar } from 'store/slices/snackbar';
import 'react-quill/dist/quill.snow.css';
import FileUploader from 'ui-component/dropzone/fileUploader';
import ConfirmatioDialog from 'ui-component/ConfirmationDialog';
import Utilities from 'utils/utilities';
import ReactQuill from 'react-quill';

interface ChildComponentProps {
    firmware?: Firmware;
    onAdd(firmware: Firmware): any;
    onClose(): any;
}

const FirmwareAdd: FC<ChildComponentProps> = (props) => {
    const { firmware, onAdd, onClose } = props;
    const [open, setOpen] = useState(false);
    const [currentFirmware, setCurrentFirmware] = useState(firmware);
    const [currentVersion, setCurrentVersion] = useState<string>('');
    const [showConfirmationProductionDialog, setShowConfirmationProductionDialog] = useState(false);
    const [showConfirmationDialog, setShowConfirmationDialog] = useState(false);
    const { t } = useTranslation();
    const theme = useTheme();
    const mobileDevice = useMediaQuery(theme.breakpoints.down('md'));
    const versionRegex = /^\d+\.\d+$/;
    const utilities = new Utilities();

    useEffect(() => {
        if (firmware === undefined) {
            setOpen(false);
        } else {
            setCurrentVersion('');
            setCurrentFirmware(firmware);
            setOpen(true);
        }
    }, [firmware]);

    const handleZipRead = (zipContent: ArrayBuffer) => {
        setCurrentFirmware((prev) => ({
            ...prev!,
            zipFile: zipContent
        }));
    };

    return (
        <>
            <Dialog sx={{ maxWidth: window.innerWidth, width: window.innerWidth - 10 }} maxWidth="lg" open={open} onClose={onClose}>
                <DialogTitle id="alert-dialog-title"> {t('ADD_NEW_FIRMWARE')} </DialogTitle>
                <DialogContent
                    sx={{
                        paddingTop: '6px!important',
                        width: mobileDevice ? window.innerWidth - 65 : '100%'
                    }}
                >
                    <Grid container>
                        <Grid item md={3} xs={6} sx={{ mt: 2 }}>
                            <FormControl fullWidth required error={!versionRegex.test(currentVersion)}>
                                <InputLabel>{t('VERSION')}</InputLabel>
                                <OutlinedInput
                                    defaultValue={utilities.getVersion(firmware?.major!, firmware?.minor!)}
                                    onChange={(d) => {
                                        const input = d.target.value;
                                        setCurrentVersion(input);

                                        if (versionRegex.test(input)) {
                                            const [major, minor] = input.split('.').map(Number);
                                            setCurrentFirmware({
                                                ...currentFirmware!,
                                                major,
                                                minor
                                            });
                                        }
                                    }}
                                    type="text"
                                    label={t('NAME')}
                                />
                                <FormHelperText>{!versionRegex.test(currentVersion) ? t('INSERT_A_VALID_VERSION') : ' '}</FormHelperText>
                            </FormControl>
                        </Grid>
                        <Grid item md={4} xs={12} sx={{ mb: 2, mt: 2, textAlign: !mobileDevice ? 'center' : 'left' }}>
                            <DateTimePicker
                                label={t('RELEASE_DATE')}
                                value={currentFirmware?.releaseDate}
                                onChange={(newDate) => {
                                    if (!newDate) return;
                                    setCurrentFirmware((prev) => ({
                                        ...prev!,
                                        releaseDate: new Date(newDate as any)
                                    }));
                                }}
                                renderInput={(params) => <TextField {...params} />}
                            />
                        </Grid>
                        <Grid item md={2} xs={12} sx={{ mb: 2, mt: 2, textAlign: !mobileDevice ? 'center' : 'left' }}>
                            <FormControl>
                                <FormControlLabel
                                    control={
                                        <Checkbox
                                            checked={currentFirmware?.internal!}
                                            onChange={(d) => {
                                                const e = {
                                                    ...currentFirmware!,
                                                    internal: d.target.checked
                                                };
                                                setCurrentFirmware(e);
                                            }}
                                        />
                                    }
                                    label={
                                        <Box>
                                            <Typography variant="body1">{t('INTERNAL_VERSION')}</Typography>
                                        </Box>
                                    }
                                    sx={{ alignItems: 'center' }}
                                />
                            </FormControl>
                        </Grid>
                        <Grid item md={12} xs={12} sx={{ mb: 2, mt: 2 }}>
                            <Box sx={{ height: '200px', paddingBottom: '40px' }}>
                                <ReactQuill
                                    className={theme.palette.mode === 'dark' ? 'dark-my-quill-editor' : 'my-quill-editor'}
                                    value={currentFirmware?.releaseNote}
                                    onChange={(d) => {
                                        const e = {
                                            ...currentFirmware!,
                                            releaseNote: d
                                        };
                                        setCurrentFirmware(e);
                                    }}
                                    theme="snow"
                                    placeholder={t('RELEASE_NOTES')}
                                    style={{ height: '100%' }}
                                />
                            </Box>
                        </Grid>
                        <Grid container>
                            <Grid item md={6} xs={12} sx={{ mb: 2, mt: 2 }}>
                                <Typography variant="body1" marginBottom="5px">
                                    {t('UPLOAD_ZIP_FILE')}*
                                </Typography>
                                <FileUploader onZipRead={handleZipRead} />
                            </Grid>
                        </Grid>
                    </Grid>
                    <Typography marginTop="20px"> * {t('MANDATORY_FIELDS')}</Typography>
                </DialogContent>
                <DialogActions sx={{ marginRight: '16px' }}>
                    <Button
                        onClick={() => {
                            setCurrentFirmware(undefined);
                            onClose();
                        }}
                        autoFocus
                        style={{
                            border: '2px solid',
                            borderColor: theme.palette.secondary.main,
                            borderRadius: 7
                        }}
                    >
                        <Typography sx={{ color: theme.palette.primary.main }}> {t('CANCEL')}</Typography>
                    </Button>
                    <Button
                        style={{
                            border: '2px solid',
                            borderColor: theme.palette.primary.main,
                            borderRadius: 7,
                            background: theme.palette.primary.main
                        }}
                        onClick={() => {
                            if (versionRegex.test(currentVersion)) {
                                if (!currentFirmware?.zipFile || currentFirmware?.zipFile?.byteLength === 0) {
                                    dispatch(startSnackbar(t('INSERT_A_ZIP_FILE'), 'error'));
                                } else if (currentFirmware?.internal) {
                                    setShowConfirmationDialog(true);
                                } else {
                                    setShowConfirmationProductionDialog(true);
                                }
                            } else {
                                dispatch(startSnackbar(t('INSERT_A_VALID_VERSION'), 'error'));
                            }
                        }}
                    >
                        <Typography style={{ color: '#fff' }}>{t('ADD')}</Typography>
                    </Button>
                </DialogActions>
            </Dialog>
            <ConfirmatioDialog
                isVisible={showConfirmationProductionDialog}
                titleText="CONFIRM_CREATION"
                bodyText="ARE_YOU_SURE_TO_CREATE_PRODUCTION_FIRMWARE"
                alertText="NOTE_THAT_YOU_WILL_NOT_BE_ABLE_TO_DELETE_IT_AFTER_CREATION"
                onClose={() => {
                    setShowConfirmationProductionDialog(false);
                }}
                onConfirm={() => {
                    setShowConfirmationProductionDialog(false);
                    onAdd(currentFirmware!);
                }}
            />
            <ConfirmatioDialog
                isVisible={showConfirmationDialog}
                titleText="CONFIRM_CREATION"
                bodyText="ARE_YOU_SURE_TO_CREATE_FIRMWARE"
                alertText="NOTE_THAT_YOU_WILL_NOT_BE_ABLE_TO_DELETE_IT_AFTER_CREATION"
                onClose={() => {
                    setShowConfirmationDialog(false);
                }}
                onConfirm={() => {
                    setShowConfirmationDialog(false);
                    onAdd(currentFirmware!);
                }}
            />
        </>
    );
};

export default FirmwareAdd;
